import React, { useCallback, useEffect, useReducer, useState } from 'react';
import * as actions from './PASActions';
import { USER_ENGAGEMENT_PAGE_KEYS } from '@silverfort-nodelibs/common/utils/consts';
import { mapToSettings, fieldsReducer, initFieldsState } from './PASReducer';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import { useSelector } from 'react-redux';
import { useTabs } from '../../hooks/useTabs';
import { CATEGORIES, SUBCATEGORIES } from '../../common/categories';
import { httpPost } from '../../../utils';
import NewCategory from '../../components/Category/NewCategory';
import JITSettings from './JITSettings';
import { Assets } from './Assets/Assets';
import { httpGet } from '../../../utils';
import { IntegrationType } from '../AlertsManagement/types';

const { PRIVILEGED_ACCESS } = CATEGORIES;
const { RESOURCES, JIT } = SUBCATEGORIES;

const PASSettings = ({ saveFunc, systemMode }) => {
    const subcategories = {
        [RESOURCES.ID]: { id: RESOURCES.ID, name: RESOURCES.NAME },
        [JIT.ID]: { id: JIT.ID, name: JIT.NAME }
    };
    const [selectedTab, setSelectedTab] = useTabs(PRIVILEGED_ACCESS, subcategories);
    const settings = useSelector((state) => state.settings.settings);
    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const [save] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedTab, dispatch);
    const [availableITSMs, setAvailableITSMs] = useState([]);
    const [itsmToType, setItsmToType] = useState(undefined);

    const fetchAvailableITSMs = useCallback(async () => {
        const result = await httpGet(`/settings/integrationsEventSchema`);
        const itsmSchemas = Object.entries(result?.data?.integrationsEventSchema?.properties)
            .filter(([integrationName, integrationProps]) => integrationProps.integrationType === IntegrationType.ITSM)
            .map(([integrationName, integrationProps]) => ({
                keyName: integrationName,
                provider: integrationProps.name
            }));
        let itsmNames = [{ value: '', title: 'None', type: '' }];
        itsmSchemas.forEach(({ keyName, provider }) => {
            if (settings.IntegrationsEventConf[keyName])
                itsmNames = [
                    ...itsmNames,
                    ...settings.IntegrationsEventConf[keyName]
                        .filter((integraion) => integraion.enabled)
                        .map((integration) => ({
                            value: integration.id,
                            title: `${provider} (${integration.name})`,
                            type: keyName
                        }))
                ];
        });
        return itsmNames;
    }, [settings.IntegrationsEventConf]);

    useEffect(async () => {
        httpPost('/data/userEngagement', { pageId: USER_ENGAGEMENT_PAGE_KEYS.SETTINGS_ASSETS });
        const availableITSMs = await fetchAvailableITSMs();
        setAvailableITSMs(availableITSMs.map((itsm) => ({ title: itsm.title, value: itsm.value })));
        setItsmToType(new Map(availableITSMs.map((itsm) => [itsm.value, itsm.type])));
    }, []);

    const handleItsmIntegrationStatus = useCallback(
        async (oldItsmID, newItsmID) => {
            // If ITSM is used for the jit we want to make sure it stays enabled and not deleted
            if (newItsmID !== '') {
                await httpPost('/settings/disableDeactivationForIntegration', {
                    providerKey: itsmToType.get(newItsmID) || '',
                    integrationId: newItsmID,
                    reason: 'Jit is using this integration for JIT policies'
                });
            }
            if (oldItsmID !== '') {
                await httpPost('/settings/enableDeactivationForIntegration', {
                    providerKey: itsmToType.get(oldItsmID) || '',
                    integrationId: oldItsmID
                });
            }
        },
        [itsmToType]
    );

    const updateITSM = useCallback(
        (oldItsmID, newItsmID) => {
            dispatch({
                type: actions.UPDATE_ITSM,
                payload: {
                    id: newItsmID,
                    type: itsmToType.get(newItsmID) || ''
                }
            });
            save();
            handleItsmIntegrationStatus(oldItsmID, newItsmID);
        },
        [save, handleItsmIntegrationStatus]
    );

    const updateADAttributes = useCallback(
        (adAttributeName, adAttributeValue) => {
            dispatch({
                type: actions.UPDATE_AD_ATTRIBUTES,
                payload: {
                    adAttribute: adAttributeName,
                    adValue: adAttributeValue
                }
            });
            save();
        },
        [save]
    );

    const updateSelectedAccounts = useCallback(
        (selectedAccounts) => {
            dispatch({
                type: actions.UPDATE_ACCOUNTS,
                payload: selectedAccounts.map((entity) => ({
                    guid: entity.guid,
                    name: entity.name,
                    domain_name: entity.domain_name,
                    type: entity.type
                }))
            });
            save();
        },
        [save]
    );

    const updateDownloadScript = useCallback(() => {
        dispatch({ type: actions.NOTIFY_DOWNLOAD_SCRIPT });
        save();
    }, [save]);

    return (
        <NewCategory
            category={PRIVILEGED_ACCESS}
            fields={subcategories}
            visibleFields={subcategories}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}>
            {selectedTab === RESOURCES.ID && (
                <Assets tiersConfig={fields.tiersConfig} settings={settings} dispatch={dispatch} save={save} />
            )}
            {selectedTab === JIT.ID && (
                <JITSettings
                    adAttribute={settings.jitCustomSettings?.adAttribute}
                    adValue={settings.jitCustomSettings?.adValue}
                    accounts={settings.jitCustomSettings?.accounts}
                    selectedITSM={settings.jitCustomSettings?.itsm}
                    availableITSMs={availableITSMs}
                    updateADAttributes={updateADAttributes}
                    updateSelectedAccounts={updateSelectedAccounts}
                    updateDownloadScript={updateDownloadScript}
                    updateITSM={updateITSM}
                />
            )}
        </NewCategory>
    );
};

export default PASSettings;
